"use client"

import { useState } from "react"
import { ChevronLeft, ChevronRight, Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import Image from "next/image"
import { Review } from "@repo/api-shared"

export function ReviewCarousel({ reviews }: { reviews: Review[] }) {
    const [currentIndex, setCurrentIndex] = useState(0)
    const itemsPerPage = 2
    const totalPages = Math.ceil(reviews.length / itemsPerPage)

    const nextSlide = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex + itemsPerPage >= reviews.length
                ? 0
                : prevIndex + itemsPerPage,
        )
    }

    const prevSlide = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex - itemsPerPage < 0
                ? reviews.length - itemsPerPage
                : prevIndex - itemsPerPage,
        )
    }

    const visibleReviews = reviews.slice(
        currentIndex,
        currentIndex + itemsPerPage,
    )

    return (
        <div className="relative">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {visibleReviews.map((review) => (
                    <Card key={review.id} className="border border-gray-200">
                        <CardContent className="p-6">
                            <div className="relative mb-4 h-60 w-full overflow-hidden md:h-72 lg:h-80">
                                <Image
                                    src={review.image_url || "/placeholder.svg"}
                                    alt={review.name}
                                    fill
                                    className="object-cover"
                                />
                            </div>
                            <div className="mb-4 flex items-center">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        className={`w-5 h-5 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
                                    />
                                ))}
                            </div>
                            <p className="mb-4">{review.text}</p>
                            <div className="font-bold">{review.name}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="mt-6 flex justify-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={prevSlide}
                        className="h-8 w-8"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="sr-only">Предыдущий</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={nextSlide}
                        className="h-8 w-8"
                    >
                        <ChevronRight className="h-4 w-4" />
                        <span className="sr-only">Следующий</span>
                    </Button>
                </div>
            )}
        </div>
    )
}
